import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { useLocalStorage } from './hooks/useLocalStorage';
import SetupTab from './components/ShopSetup/SetupTab';
import WorkflowTab from './components/Workflow/WorkflowTab';
import ResearchTab from './components/Research/ResearchTab';
import UserDirectory from './components/Admin/UserDirectory';
import ShopSelector from './components/ShopSetup/ShopSelector';
import Login from './components/Auth/Login';
import AccountDropdown from './components/Auth/AccountDropdown';
import SafeIcon from './common/SafeIcon';
import { INITIAL_PROTOCOLS, WORKFLOW_STAGES, SETUP_CHECKLIST } from './data/constants';
import { FiLayout, FiCheckSquare, FiRefreshCw, FiTrendingUp, FiLogOut, FiCloud, FiCloudOff, FiUser, FiClock } from 'react-icons/fi';

const INACTIVITY_LIMIT = 30 * 60 * 1000;

function App() {
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('setup');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCloudSynced, setIsCloudSynced] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const timeoutRef = useRef(null);
  const [shops, setShops] = useState([]);
  const [activeShopId, setActiveShopId] = useState(null);
  const [products, setProducts] = useState([]);
  const [protocols, setProtocols] = useLocalStorage('pod-studio-protocols', INITIAL_PROTOCOLS);
  const [designCounter, setDesignCounter] = useState(1);
  const [targets, setTargets] = useState({ weekly: 5, monthly: 20 });

  useEffect(() => {
    if (!protocols || !Array.isArray(protocols)) {
      setProtocols(INITIAL_PROTOCOLS);
    }
  }, []);

  const handleLogout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    supabase.auth.signOut();
  }, []);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (session) {
      timeoutRef.current = setTimeout(() => handleLogout(), INACTIVITY_LIMIT);
    }
  }, [session, handleLogout]);

  useEffect(() => {
    if (!session) return;
    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    resetTimer();
    activities.forEach(event => window.addEventListener(event, resetTimer));
    return () => {
      activities.forEach(event => window.removeEventListener(event, resetTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [session, resetTimer]);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data } = await supabase.from('profiles_1716182400000').select('*').eq('id', userId).single();
      if (data) {
        setUserRole(data.role);
        setProfile(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchCloudData = useCallback(async () => {
    setIsSyncing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await fetchProfile(user.id);
      
      const { data: shopData } = await supabase.from('pods_shops_1740661111000').select('*');
      if (shopData && shopData.length > 0) {
        setShops(shopData);
        setActiveShopId(shopData[0].id);
      } else {
        const { data: newShop } = await supabase.from('pods_shops_1740661111000').insert([
          { name: 'My First Shop', type: 'etsy', user_id: user.id }
        ]).select().single();
        if (newShop) {
          setShops([newShop]);
          setActiveShopId(newShop.id);
        }
      }

      const { data: productData } = await supabase.from('pods_products_1740661111000').select('*');
      if (productData) {
        const mappedProducts = productData.map(p => ({ ...p.data, id: p.id }));
        setProducts(mappedProducts);
        const maxNum = mappedProducts.reduce((acc, p) => {
          const num = parseInt(p.designNumber?.split('-')[1]);
          return isNaN(num) ? acc : Math.max(acc, num);
        }, 0);
        setDesignCounter(maxNum + 1);
      }
      setIsCloudSynced(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  }, [fetchProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchCloudData();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchCloudData();
    });
    return () => subscription.unsubscribe();
  }, [fetchCloudData]);

  const handleAddProduct = async (newProductData) => {
    setIsSyncing(true);
    const { data: newRecord } = await supabase.from('pods_products_1740661111000').insert([
      { data: newProductData, user_id: session.user.id }
    ]).select().single();
    if (newRecord) {
      setProducts(prev => [...prev, { ...newRecord.data, id: newRecord.id }]);
      setDesignCounter(prev => prev + 1);
    }
    setIsSyncing(false);
  };

  const handleUpdateProduct = async (id, incomingUpdate) => {
    setIsSyncing(true);
    const existingProduct = products.find(p => p.id === id);
    if (!existingProduct) {
      setIsSyncing(false);
      return;
    }
    const { id: _, ...cleanIncoming } = incomingUpdate;
    const mergedData = { ...existingProduct, ...cleanIncoming };
    const { id: __, ...dataToSave } = mergedData;
    const { error } = await supabase.from('pods_products_1740661111000').update({ data: dataToSave }).eq('id', id);
    if (!error) {
      setProducts(prev => prev.map(p => p.id === id ? mergedData : p));
    }
    setIsSyncing(false);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this design?')) return;
    setIsSyncing(true);
    const { error } = await supabase.from('pods_products_1740661111000').delete().eq('id', id);
    if (!error) {
      setProducts(prev => prev.filter(p => p.id !== id));
    }
    setIsSyncing(false);
  };

  const handleMoveProduct = async (id, direction) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const currentIndex = WORKFLOW_STAGES.indexOf(product.status);
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < WORKFLOW_STAGES.length) {
      handleUpdateProduct(id, { status: WORKFLOW_STAGES[newIndex] });
    }
  };

  const updateShopProgress = async (newProgress) => {
    const updatedShops = shops.map(shop => shop.id === activeShopId ? { ...shop, progress: newProgress } : shop);
    setShops(updatedShops);
    const currentShop = updatedShops.find(s => s.id === activeShopId);
    if (currentShop) {
      setIsSyncing(true);
      await supabase.from('pods_shops_1740661111000').update({ progress: newProgress, updated_at: new Date() }).eq('id', currentShop.id);
      setIsSyncing(false);
    }
  };

  if (!session) return <Login />;

  const activeShop = shops.find(s => s.id === activeShopId) || shops[0];

  // FIXED: Dynamic completion calculation
  const calculateSetupCompletion = () => {
    if (!activeShop) return 0;
    
    // Calculate total items based on current shop type
    const dynamicTotal = SETUP_CHECKLIST.reduce((acc, section) => {
      if (activeShop.type === 'etsy' && section.id === 'shopify-setup') return acc;
      if (activeShop.type === 'shopify' && section.id === 'etsy-setup') return acc;
      return acc + section.items.length;
    }, 0);

    const progress = activeShop.progress || {};
    let completed = 0;
    
    Object.values(progress).forEach(section => {
      Object.values(section).forEach(data => {
        if (typeof data === 'object' ? data.completed : !!data) completed++;
      });
    });

    return dynamicTotal > 0 ? Math.min(Math.round((completed / dynamicTotal) * 100), 100) : 0;
  };

  const availableTabs = [
    { id: 'setup', label: 'Checklist' },
    { id: 'workflow', label: 'Workflow' },
    ...(userRole === 'admin' ? [
      { id: 'research', label: 'Protocol' },
      { id: 'admin', label: 'Admin Directory' }
    ] : [])
  ];

  return (
    <div className="min-h-screen flex flex-col bg-cream/20">
      <header className="bg-espresso text-cream sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-6">
            <div className="flex flex-col gap-6 w-full md:w-auto">
              <div className="flex items-center justify-between md:justify-start gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-terracotta rounded-lg flex items-center justify-center shadow-inner">
                    <SafeIcon icon={FiLayout} className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold leading-tight">POD Studio</h1>
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] text-cream/50 uppercase tracking-widest">{userRole} Workspace</p>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border flex items-center gap-1 ${isCloudSynced ? 'bg-sage/20 border-sage/30 text-sage' : 'bg-terracotta/20 border-terracotta/30 text-terracotta'}`}>
                        <SafeIcon icon={isCloudSynced ? FiCloud : FiCloudOff} className="w-2 h-2" />
                        {isCloudSynced ? 'CLOUD SYNCED' : 'SYNCING...'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:hidden">
                  <button onClick={fetchCloudData} className={`p-2 ${isSyncing ? 'animate-spin text-terracotta' : 'text-cream/40'}`}>
                    <SafeIcon icon={FiRefreshCw} />
                  </button>
                  <button onClick={() => setIsAccountOpen(!isAccountOpen)} className="p-2 text-cream/40 hover:text-white">
                    <SafeIcon icon={FiUser} />
                  </button>
                </div>
              </div>
              <div className="w-full max-w-sm">
                <ShopSelector 
                  shops={shops} 
                  activeShopId={activeShopId} 
                  setActiveShopId={setActiveShopId} 
                  addShop={async (name, type) => {
                    const { data } = await supabase.from('pods_shops_1740661111000').insert([{ name, type, user_id: session.user.id }]).select().single();
                    if (data) {
                      setShops([...shops, data]);
                      setActiveShopId(data.id);
                    }
                  }} 
                  deleteShop={async (id) => {
                    if (shops.length <= 1) return;
                    await supabase.from('pods_shops_1740661111000').delete().eq('id', id);
                    const remaining = shops.filter(s => s.id !== id);
                    setShops(remaining);
                    if (activeShopId === id) setActiveShopId(remaining[0].id);
                  }}
                  updateShopName={async (id, name) => {
                    await supabase.from('pods_shops_1740661111000').update({ name }).eq('id', id);
                    setShops(s => s.map(x => x.id === id ? { ...x, name } : x));
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 self-end md:self-start">
              <div className="flex items-center gap-6 text-sm bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-sm shadow-xl">
                <div className="flex flex-col items-center">
                  <span className="text-cream/50 text-[10px] uppercase tracking-wider mb-1">Setup</span>
                  <div className="flex items-center gap-2 font-bold text-sage">
                    <SafeIcon icon={FiCheckSquare} />
                    {calculateSetupCompletion()}%
                  </div>
                </div>
                <div className="w-px h-8 bg-cream/20"></div>
                <div className="flex flex-col items-center">
                  <span className="text-cream/50 text-[10px] uppercase tracking-wider mb-1">Pipeline</span>
                  <div className="flex items-center gap-2 font-bold text-terracotta">
                    <SafeIcon icon={FiTrendingUp} />
                    {products.length}
                  </div>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2 relative">
                <button onClick={fetchCloudData} className={`p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all border border-white/10 ${isSyncing ? 'animate-spin text-terracotta' : 'text-cream/40 hover:text-white'}`}>
                  <SafeIcon icon={FiRefreshCw} />
                </button>
                <div className="relative">
                  <button onClick={() => setIsAccountOpen(!isAccountOpen)} className="flex items-center gap-3 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-all group">
                    <div className="w-8 h-8 rounded-lg bg-terracotta overflow-hidden shadow-lg border border-white/10">
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="text-white w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="text-[10px] font-bold text-white leading-tight truncate max-w-[80px]">
                        {profile?.full_name || 'Account'}
                      </p>
                      <p className="text-[8px] text-white/40 uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiClock} className="w-2 h-2" /> 30m Auto-Logout
                      </p>
                    </div>
                    <SafeIcon icon={FiLogOut} className="text-white/20 group-hover:text-red-400 transition-colors ml-1" onClick={(e) => { e.stopPropagation(); handleLogout(); }} />
                  </button>
                  <AccountDropdown isOpen={isAccountOpen} setIsOpen={setIsAccountOpen} user={session.user} profile={profile} onUpdate={() => fetchProfile(session.user.id)} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-8 border-t border-cream/10 pt-2 overflow-x-auto no-scrollbar">
            {availableTabs.map(tab => (
              <button 
                key={tab.id} 
                onClick={() => setActiveTab(tab.id)} 
                className={`pb-3 px-2 font-bold transition-colors border-b-2 text-xs uppercase tracking-wider whitespace-nowrap ${activeTab === tab.id ? 'border-terracotta text-white' : 'border-transparent text-cream/60 hover:text-cream'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          {activeTab === 'setup' && <SetupTab shop={activeShop} progress={activeShop?.progress || {}} setProgress={updateShopProgress} />}
          {activeTab === 'workflow' && (
            <WorkflowTab 
              products={products} 
              onAddProduct={handleAddProduct} 
              onUpdateProduct={handleUpdateProduct} 
              onDeleteProduct={handleDeleteProduct} 
              onMoveProduct={handleMoveProduct} 
              targets={targets} 
              setTargets={setTargets} 
              designCounter={designCounter} 
              shops={shops} 
              activeShopId={activeShopId} 
              protocols={protocols}
            />
          )}
          {activeTab === 'research' && userRole === 'admin' && (
            <ResearchTab protocols={protocols} setProtocols={setProtocols} />
          )}
          {activeTab === 'admin' && userRole === 'admin' && <UserDirectory />}
        </div>
      </main>
    </div>
  );
}

export default App;